/**
 * The duel match core: the ephemeral, server-orchestrated one-versus-one match
 * modelled on the Rat Swarm event state (PRD #252, Slices C-D).
 *
 * A duel is a player-initiated fight to the death, optionally for a Gold wager.
 * Like {@link RatSwarmState} this state is **ephemeral server memory beside the
 * world** (ADR-0117 / ADR-0130): it never persists, never enters a snapshot, and
 * keys off entity ids rather than world components. What lives here is the pure,
 * clock-free *decision* layer - the match shape, its four-phase lifecycle, the
 * target-eligibility guard, and the pending-challenge timeout counter - exactly
 * the way `ratSwarm.ts` owns spawn/skitter/resolve/timeout while the server owns
 * the acts (receiving commands, moving gold, broadcasting). Nothing here reads a
 * wall clock or a socket; the server feeds it tick counts and the flags it alone
 * can compute (who owns which entity, who is already dueling).
 *
 * Slice C (ADR-0130) built the whole *front of flow* - challenge, target, wager,
 * accept - and Slice D (ADR-0131) fills in the fight: the pure, clock-free
 * {@link stepDuel} resolver drives all four phases off the tick (countdown,
 * combat, death, ending) and returns typed {@link DuelEffect}s the server enacts
 * (applying damage to replicated health, broadcasting, moving gold). The
 * escrow/refund/payout money moves live server-side in `server/duelEscrow.ts`,
 * against the account store, since they touch durable gold (a server concern).
 *
 * See ADR-0130 and ADR-0131.
 */
import type { EntityId } from './entity';

// ---------------------------------------------------------------------------
// Tunables. Every knob of the handshake, in one place (PRD #252's values).
// ---------------------------------------------------------------------------

/**
 * Max Chebyshev distance (the 8-directional metric) a challenge reaches: a duel
 * is a face-to-face call-out, not a map-wide snipe (PRD story 4).
 */
export const DUEL_CHALLENGE_RANGE = 6;

/**
 * How long a pending challenge waits for an answer before it auto-declines, in
 * **ticks** - thirty seconds' worth of the server's 50ms fixed steps (`STEP_MS`,
 * `server/gameServer.ts`). Expressed as ticks, never milliseconds, because the
 * core is clock-free: the server feeds {@link stepPendingChallenge} one call per
 * tick and the timeout falls out of counting, exactly like the swarm's duration.
 */
export const DUEL_CHALLENGE_TIMEOUT_TICKS = 600;

// ---------------------------------------------------------------------------
// The match model.
// ---------------------------------------------------------------------------

/**
 * How far a duel has progressed. `pending` is a challenge awaiting the target's
 * answer; `countdown` is the five-count over both heads; `fighting` is live
 * combat; `ending` is the death beat before teardown. {@link stepDuel} drives the
 * whole lifecycle off the tick (Slice D, ADR-0131); Slice C only ever reached
 * `pending`. The Rat Swarm's `status` field is the precedent, widened to the
 * duel's phases.
 */
export type DuelPhase = 'pending' | 'countdown' | 'fighting' | 'ending';

/**
 * One duel, as ephemeral server memory beside the world. The two participants
 * are named by entity id (the server maps a connection to its owned entity, so
 * ownership stays the server's concern); `wager` is the gold each side stakes
 * (0 = an honor duel that escrows nothing). Mutated only by the pure helpers
 * here and the server's off-queue handlers; never persisted, never snapshotted.
 */
export interface DuelMatch {
  /** The entity that issued the challenge. */
  challenger: EntityId;
  /** The entity being challenged. */
  target: EntityId;
  /** Gold each side stakes; the pot is `2 * wager`. `0` = honor duel. */
  wager: number;
  /** Where the match is in its lifecycle, advanced by {@link stepDuel}. */
  phase: DuelPhase;
  /**
   * Ticks elapsed since the challenge was issued, advanced by
   * {@link stepPendingChallenge} while `phase === 'pending'`; the pending-timeout
   * auto-decline fires when it reaches {@link DUEL_CHALLENGE_TIMEOUT_TICKS}.
   */
  pendingElapsedTicks: number;
  /**
   * The live combat sub-state, attached by {@link startDuelCountdown} on accept
   * and absent while the match is still `pending`.
   * Additive-optional so {@link newPendingMatch} stays the lean challenge object;
   * see {@link DuelFight}.
   */
  fight?: DuelFight;
}

/**
 * Chebyshev distance: the natural range metric for 8-directional movement (the
 * same metric the swarm's stomp range and the door-reach gate use). Exported so
 * the server's target guard and the client's blue-hover selection share one
 * definition rather than each re-deriving `Math.max(|dx|, |dy|)`.
 */
export function chebyshev(ax: number, ay: number, bx: number, by: number): number {
  return Math.max(Math.abs(ax - bx), Math.abs(ay - by));
}

// ---------------------------------------------------------------------------
// Target eligibility (the pure guard the server composes flags for).
// ---------------------------------------------------------------------------

/**
 * Everything the target guard needs, computed **server-side** from the live world
 * and the session registry and handed in as plain flags, so the guard itself is a
 * pure function of primitives (node-testable with no `World`). The server alone
 * can tell a player from an NPC (a player entity is one a live session owns; an
 * NPC is a socket-less roster mobile with no session) or a ghost (a downed
 * duelist - Slice E's death now DOES swap the loser onto a transient ghost body,
 * but see `isGhost` below for why the guard still leaves that flag false), so it
 * classifies and passes booleans - mirroring how `resolveStomp` takes a server-built
 * {@link StompAttempt} rather than reaching for sockets itself.
 */
export interface DuelTargetContext {
  /** The pick names the challenger's own entity. */
  isSelf: boolean;
  /** A giant rat (carries the `rat` marker) - vermin, never a duel opponent. */
  isRat: boolean;
  /**
   * A downed/ghost duelist. Slice E's death now swaps the loser onto a transient
   * ghost body, yet this stays hardcoded `false`: a downed duelist is still
   * mid-match, so `targetBusy` already makes them unpickable regardless. The slot
   * is kept as a deliberate defensive/redundant no-op; wiring real ghost-body
   * detection here is out of scope.
   */
  isGhost: boolean;
  /** A live player: some connected session owns this entity. */
  isPlayer: boolean;
  /** Chebyshev tiles between challenger and target. */
  distance: number;
  /** The challenger is already in a duel or challenge (must be free to call one out). */
  challengerBusy: boolean;
  /** The target is already dueling, already challenged, or mid-countdown. */
  targetBusy: boolean;
}

/** Why a duel target was refused. Every case fizzles silently for the picker. */
export type DuelTargetRejection =
  | 'self'
  | 'is-rat'
  | 'is-ghost'
  | 'is-npc'
  | 'out-of-range'
  | 'challenger-busy'
  | 'target-busy';

/** The result of the target guard: eligible, or refused with a reason. */
export type DuelTargetOutcome = { ok: true } | { ok: false; reason: DuelTargetRejection };

/**
 * Decide whether a duel challenge may be issued against a target (PRD stories
 * 4, 30, 31). A valid target is a live player within {@link DUEL_CHALLENGE_RANGE}
 * (Chebyshev) who is not the challenger, a rat, an NPC, or a ghost, and who is
 * not already dueling / challenged / mid-countdown; the challenger must likewise
 * be free. The checks run cheapest-and-most-specific first so a rejection names
 * the actual reason (an NPC pick reads `is-npc`, not `out-of-range`), which the
 * server logs; to the picker every failure is the same silent fizzle.
 *
 * Anything that is neither the challenger, a rat, a ghost, nor a live player is
 * an NPC (a socket-less roster mobile) or a non-mobile prop - both `is-npc`, the
 * catch-all for "not a real opponent".
 */
export function checkDuelTarget(ctx: DuelTargetContext): DuelTargetOutcome {
  if (ctx.isSelf) return { ok: false, reason: 'self' };
  if (ctx.isRat) return { ok: false, reason: 'is-rat' };
  if (ctx.isGhost) return { ok: false, reason: 'is-ghost' };
  if (!ctx.isPlayer) return { ok: false, reason: 'is-npc' };
  if (ctx.distance > DUEL_CHALLENGE_RANGE) return { ok: false, reason: 'out-of-range' };
  if (ctx.challengerBusy) return { ok: false, reason: 'challenger-busy' };
  if (ctx.targetBusy) return { ok: false, reason: 'target-busy' };
  return { ok: true };
}

// ---------------------------------------------------------------------------
// The pending-challenge lifecycle.
// ---------------------------------------------------------------------------

/**
 * A fresh pending challenge from `challenger` to `target` for `wager` gold. The
 * single entrypoint the server calls after a target passes {@link checkDuelTarget}
 * and the wager passes escrow validation; the match starts in `pending` with its
 * timeout clock at zero.
 */
export function newPendingMatch(
  challenger: EntityId,
  target: EntityId,
  wager: number,
): DuelMatch {
  return { challenger, target, wager, phase: 'pending', pendingElapsedTicks: 0 };
}

/**
 * Advance a pending challenge's timeout clock by one tick and report whether it
 * has now expired (PRD story 9: an unanswered challenge auto-declines after
 * thirty seconds). A no-op that never expires once the match has left `pending`
 * (accepted / declined), so a stray tick after teardown cannot fire a second
 * timeout. The server tears the match down and notifies the challenger on a
 * `true` return - the same shape `stepRatSwarm` uses to report a timeout end.
 */
export function stepPendingChallenge(match: DuelMatch): { expired: boolean } {
  if (match.phase !== 'pending') return { expired: false };
  match.pendingElapsedTicks += 1;
  return { expired: match.pendingElapsedTicks >= DUEL_CHALLENGE_TIMEOUT_TICKS };
}

// ===========================================================================
// The fight core (Duel slice D, PRD #252, ADR-0131).
//
// Once a challenge is accepted and both stakes are escrowed (server acts), the
// match leaves `pending` and the pure fight resolver drives the rest: a five-
// count over both heads, the visible combat stance, the adjacency-gated auto-
// swing loop, damage against replicated health, death at zero, the winner, and
// the no-damage draw watchdog. Exactly like {@link resolveStomp}/`stepRatSwarm`,
// this layer only **decides** - it advances tick counters and reports typed
// {@link DuelEffect}s; the server owns the **acts** (applying damage to the
// replicated health component, broadcasting animations/sounds/floats, moving
// gold via `duelEscrow`). Randomness is **injected** (`rng`), never the seeded
// world RNG: a duel is a non-deterministic, non-sim event whose outcomes are
// broadcast authoritatively - the same server-randomness diversion Stomp/dice
// use. Every function here reads no wall clock and no unseeded randomness, so it
// is unit-tested with explicit ticks and a scripted `rng` (the dice/NPC pattern).
// ===========================================================================

// ---------------------------------------------------------------------------
// Fight tunables (all PRD #252 starting values; every one adjustable here).
// ---------------------------------------------------------------------------

/** How many digits the opening count ticks down (5, 4, 3, 2, 1). */
export const DUEL_COUNTDOWN_COUNT = 5;

/**
 * Ticks each countdown digit is shown - one second's worth of the server's 50ms
 * fixed steps (`STEP_MS`), so 20 ticks per digit; the whole five-count runs
 * {@link DUEL_COUNTDOWN_TICKS}. Expressed in ticks (never ms) because the core is
 * clock-free.
 */
export const DUEL_COUNTDOWN_TICKS_PER_COUNT = 20;

/** The whole opening five-count, in ticks (five seconds). */
export const DUEL_COUNTDOWN_TICKS = DUEL_COUNTDOWN_COUNT * DUEL_COUNTDOWN_TICKS_PER_COUNT;

/**
 * Ticks between a player's auto-swings - 1.2 seconds of 50ms steps (24 ticks).
 * Both players swing on this fixed cadence; a swing resolves only when adjacent
 * (see {@link stepDuel}), so the cadence is constant but landing depends on
 * position. Tuned down from the original 40 (2s) to make combat feel faster and
 * punchier - a starter value the captain will re-tune after playtest (PRD #252).
 */
export const DUEL_SWING_INTERVAL_TICKS = 24;

/** Probability in `[0, 1)` that a resolved (in-range) swing lands. */
export const DUEL_HIT_CHANCE = 0.5;

/**
 * Inclusive uniform damage range a landed hit rolls (5..30). Tuned up from the
 * original 3..12, then 5..18, for faster, swingier fights: a wide 5..30 spread
 * means a single lucky hit can swing a duel (big-hit / comeback moments) - a
 * starter value the captain will re-tune after playtest (PRD #252).
 */
export const DUEL_DAMAGE_MIN = 5;
export const DUEL_DAMAGE_MAX = 30;

/**
 * Ticks of **no landed damage** before a duel expires as a draw - thirty seconds
 * of 50ms steps. Reset to zero on every hit; two players who only ever kite each
 * other cannot lock the stakes up forever (PRD story 27).
 */
export const DUEL_NO_DAMAGE_DRAW_TICKS = 600;

/**
 * The death beat: ticks the loser lies dead before being resurrected and the
 * wager paid out - about two and a half seconds of 50ms steps (PRD story 25).
 */
export const DUEL_DEATH_BEAT_TICKS = 50;

/**
 * Max Chebyshev distance at which a swing lands - adjacency. A duel swing is a
 * punch, so the attacker must be within one tile (king-move adjacent, or on the
 * same tile) of the opponent at swing time or the swing is skipped.
 */
export const DUEL_SWING_RANGE = 1;

/**
 * Max Chebyshev distance at which a **bow shot** can be fired (Fire your bow!,
 * ADR-0255). A duel's auto-swings are a punch, so they only land while adjacent
 * ({@link DUEL_SWING_RANGE}); when the opponent kites out to more than a tile the
 * melee stops and, historically, the fight drifted to a no-damage draw and a full
 * refund - a runner's free escape. The bow closes that window: the player may
 * manually fire (F) at a fleeing opponent up to this range for the same 50/50
 * hit and the same damage as a punch. Wider than the {@link DUEL_CHALLENGE_RANGE}
 * (6) so a bowman still has reach once the runner has opened distance.
 */
export const DUEL_BOW_RANGE = 10;

/**
 * Ticks a duelist must wait between bow shots - the same cadence as an auto-swing
 * ({@link DUEL_SWING_INTERVAL_TICKS}, ~1.2s), so a bowman cannot out-DPS a melee
 * fighter by spamming F. The cooldown is per-shooter fight state (decremented each
 * tick by {@link stepDuel}) rather than a wall clock, so it stays deterministic and
 * unit-testable like the rest of the resolver.
 */
export const DUEL_BOW_COOLDOWN_TICKS = DUEL_SWING_INTERVAL_TICKS;

/**
 * The live combat sub-state of a match, created when a challenge is accepted
 * ({@link startDuelCountdown}) and dropped at teardown. Kept **separate** from
 * the always-present pending fields so {@link newPendingMatch} stays the lean
 * challenge object Slice C pins, and present only once combat is under way -
 * additive-optional, the codebase's house style. All counters are in ticks; the
 * server feeds one {@link stepDuel} per tick and the whole fight falls out of
 * counting.
 */
export interface DuelFight {
  /** Ticks elapsed in the current timed phase (the countdown, or the ending beat). */
  phaseTicks: number;
  /** Highest countdown digit already emitted (5..1), so each new second emits once; 0 = none yet. */
  lastCount: number;
  /** Ticks since the challenger last swung, counting to {@link DUEL_SWING_INTERVAL_TICKS}. */
  challengerSwingTicks: number;
  /** Ticks since the target last swung. */
  targetSwingTicks: number;
  /** Ticks since the last **landed** damage; the no-damage draw watchdog. */
  noDamageTicks: number;
  /** Ticks the challenger must wait before firing another bow shot; 0 = ready ({@link DUEL_BOW_COOLDOWN_TICKS}). */
  challengerBowCooldownTicks: number;
  /** Ticks the target must wait before firing another bow shot; 0 = ready. */
  targetBowCooldownTicks: number;
  /**
   * Whether the challenger's **bow auto-fire** is armed (Auto-fire the duel bow):
   * pressing F toggles this, and while it is on the server looses a bow shot every
   * {@link DUEL_BOW_COOLDOWN_TICKS} on its own - the ranged twin of the always-on
   * auto-swing (the "wrestling auto-punch") - so the shooter never mashes F. Off by
   * default; a fired shot still passes the same range/cooldown gates, so an armed
   * bow out of range simply skips (the auto-swing's out-of-range discipline).
   */
  challengerBowAutoFire: boolean;
  /** Whether the target's bow auto-fire is armed; see {@link challengerBowAutoFire}. */
  targetBowAutoFire: boolean;
  /**
   * Total damage the challenger has **taken** this fight (from either a landed
   * melee swing or a landed bow shot), and the same for the target. Ephemeral
   * per-match state the server accumulates where it applies damage
   * (`server/events/duel.ts`); read **once** at the fought-out decided end to
   * award **Flawless Victory** to a winner who took zero damage (PRD #669 Slice 3).
   * Never persisted, never snapshotted.
   */
  challengerDamageTaken: number;
  /** Total damage the target has taken this fight; see {@link challengerDamageTaken}. */
  targetDamageTaken: number;
  /**
   * Count of **melee** swings the challenger has **landed** this fight (the
   * `stepDuel` auto-swing path only - bow shots via `fireDuelBow` are a distinct
   * path, ADR-0279, and are deliberately not counted). The same for the target.
   * Read once at the fought-out decided end to award **Deadeye** to a winner who
   * landed zero melee swings - i.e. won purely by the bow (PRD #669 Slice 3).
   */
  challengerMeleeSwingsLanded: number;
  /** Count of melee swings the target has landed; see {@link challengerMeleeSwingsLanded}. */
  targetMeleeSwingsLanded: number;
  /** The winner once a player has died (the payout target); `null` until then / on a draw. */
  winner: EntityId | null;
  /** The loser once a player has died (the resurrection target); `null` until then / on a draw. */
  loser: EntityId | null;
}

/**
 * Everything one {@link stepDuel} needs about the live world, computed
 * **server-side** and handed in as primitives so the resolver stays pure: whether
 * the two duelists are within {@link DUEL_SWING_RANGE} (Chebyshev) right now, and
 * each participant's current replicated health (the server reads it off the
 * health component, applies the damage this call returns, and death falls out of
 * the projection consistently).
 */
export interface DuelTickInput {
  /** Challenger and target within {@link DUEL_SWING_RANGE} at this tick. */
  adjacent: boolean;
  /** The challenger's current health (server-read from the replicated component). */
  challengerHealth: number;
  /** The target's current health. */
  targetHealth: number;
}

/** One resolved (in-range) swing: who hit whom, whether it landed, and for how much. */
export interface DuelSwing {
  /** The swinging entity. */
  attacker: EntityId;
  /** The entity swung at. */
  defender: EntityId;
  /** Whether the swing landed (a 50/50 draw). */
  hit: boolean;
  /** Damage dealt on a hit; `0` on a miss. */
  damage: number;
}

/**
 * A decision this tick for the server to enact. `count` floats a countdown digit
 * over both heads; `combat-open` sets the replicated stance / war cursor on both
 * and privately shows each duelist their own red "attacking you" line (localized
 * to the victim, not broadcast to the room); `swing` is a resolved swing (hit or miss) to
 * animate, sound, and (on a hit) apply damage for; `death` opens the death beat
 * (the loser's die clip + the whole-bank winner journal line); `end` tears the
 * match down (a win pays the winner out, a draw refunds both, and either way both
 * are resurrected to full and their stance/cursor dropped).
 */
export type DuelEffect =
  | { kind: 'count'; value: number }
  | { kind: 'combat-open' }
  | { kind: 'swing'; swing: DuelSwing }
  | { kind: 'death'; winner: EntityId; loser: EntityId }
  | { kind: 'end'; outcome: 'win' | 'draw'; winner: EntityId | null; loser: EntityId | null };

/**
 * Begin the fight on accept: leave `pending`, enter the `countdown` phase, and
 * attach a fresh {@link DuelFight}. The single entrypoint the server calls once
 * both stakes are escrowed; from here {@link stepDuel} drives the lifecycle. The
 * pending timeout clock is left as-is (harmless; {@link stepPendingChallenge} is
 * a no-op off `pending`).
 */
export function startDuelCountdown(match: DuelMatch): void {
  match.phase = 'countdown';
  match.fight = {
    phaseTicks: 0,
    lastCount: 0,
    challengerSwingTicks: 0,
    targetSwingTicks: 0,
    noDamageTicks: 0,
    challengerBowCooldownTicks: 0,
    targetBowCooldownTicks: 0,
    challengerBowAutoFire: false,
    targetBowAutoFire: false,
    challengerDamageTaken: 0,
    targetDamageTaken: 0,
    challengerMeleeSwingsLanded: 0,
    targetMeleeSwingsLanded: 0,
    winner: null,
    loser: null,
  };
}

/** Roll one swing's hit/miss and (on a hit) its damage from the injected `rng`. */
function resolveSwing(attacker: EntityId, defender: EntityId, rng: () => number): DuelSwing {
  const hit = rng() < DUEL_HIT_CHANCE;
  const damage = hit
    ? DUEL_DAMAGE_MIN + Math.floor(rng() * (DUEL_DAMAGE_MAX - DUEL_DAMAGE_MIN + 1))
    : 0;
  return { attacker, defender, hit, damage };
}

/**
 * Advance one accepted duel by a single server tick and report the effects to
 * enact (PRD #252). Drives the whole post-accept lifecycle:
 *
 *  - **countdown**: emits one `count` digit per second (5..1); at zero (the bell)
 *    opens combat (`combat-open`) and each participant's first swing fires on that
 *    same tick (adjacency- and hit-gated as always), the fight running from there.
 *  - **fighting**: advances both swing timers; when one fires, the swing resolves
 *    **only if `adjacent`** (else it is silently skipped - movement gates the
 *    fight), a 50/50 `hit` for {@link DUEL_DAMAGE_MIN}..{@link DUEL_DAMAGE_MAX}
 *    damage. Damage is applied to a local projection of health so a swing that
 *    brings the defender to zero yields a `death` (winner = the survivor) and no
 *    further swing lands that tick. With no landed damage for
 *    {@link DUEL_NO_DAMAGE_DRAW_TICKS} the match `end`s as a draw.
 *  - **ending**: after {@link DUEL_DEATH_BEAT_TICKS} it `end`s as a win.
 *
 * A no-op (empty array) while `pending` or with no {@link DuelFight} attached, so
 * a stray call before accept or after teardown is harmless. Randomness comes only
 * from `rng`; the server passes its own `Math.random`-based source and a test
 * pins a scripted one.
 */
export function stepDuel(match: DuelMatch, input: DuelTickInput, rng: () => number): DuelEffect[] {
  const fight = match.fight;
  if (!fight) return [];
  const effects: DuelEffect[] = [];

  if (match.phase === 'countdown') {
    fight.phaseTicks += 1;
    // The digit for this tick: 5 on the first tick, then one lower each second.
    const elapsedCounts = Math.floor((fight.phaseTicks - 1) / DUEL_COUNTDOWN_TICKS_PER_COUNT);
    const digit = DUEL_COUNTDOWN_COUNT - elapsedCounts;
    if (digit >= 1 && digit !== fight.lastCount) {
      fight.lastCount = digit;
      effects.push({ kind: 'count', value: digit });
    }
    if (fight.phaseTicks < DUEL_COUNTDOWN_TICKS) return effects;
    // The bell (countdown 0): combat opens. Each participant's FIRST swing lands
    // on the bell - the same tick the combat stance / war cursor / "attacking you"
    // line trigger - not one full interval later. Prime both swing timers to a full
    // interval so the fighting pass below (which runs this very tick - note the
    // fall-through, no early return) fires the opening swings now, then they reset
    // to zero and continue on the normal cadence. The opening swings still respect
    // the adjacency gate and still 50/50 to hit; only their timing is immediate.
    match.phase = 'fighting';
    fight.challengerSwingTicks = DUEL_SWING_INTERVAL_TICKS;
    fight.targetSwingTicks = DUEL_SWING_INTERVAL_TICKS;
    fight.noDamageTicks = 0;
    effects.push({ kind: 'combat-open' });
    // Fall through into the fighting branch: the bell tick is the first fight tick.
  }

  if (match.phase === 'fighting') {
    // Local projection of both healths: the server holds the authoritative
    // replicated component and applies the damage this call returns, but the
    // resolver decides death off the same numbers so the two never diverge.
    let challengerHp = input.challengerHealth;
    let targetHp = input.targetHealth;

    fight.challengerSwingTicks += 1;
    fight.targetSwingTicks += 1;
    fight.noDamageTicks += 1;
    // Tick each duelist's bow cooldown down toward ready (ADR-0255). A manual
    // `fireDuelBow` sets it to a full interval; it counts back to 0 here so the
    // next shot is gated on the melee cadence, deterministically and off any clock.
    if (fight.challengerBowCooldownTicks > 0) fight.challengerBowCooldownTicks -= 1;
    if (fight.targetBowCooldownTicks > 0) fight.targetBowCooldownTicks -= 1;

    // The challenger swings first (server processes serially); a swing that kills
    // the target means the (now dead) target cannot swing back this tick.
    if (fight.challengerSwingTicks >= DUEL_SWING_INTERVAL_TICKS) {
      fight.challengerSwingTicks = 0;
      if (input.adjacent && challengerHp > 0 && targetHp > 0) {
        const swing = resolveSwing(match.challenger, match.target, rng);
        effects.push({ kind: 'swing', swing });
        if (swing.hit) {
          targetHp = Math.max(0, targetHp - swing.damage);
          fight.noDamageTicks = 0;
        }
      }
    }
    if (fight.targetSwingTicks >= DUEL_SWING_INTERVAL_TICKS) {
      fight.targetSwingTicks = 0;
      if (input.adjacent && targetHp > 0 && challengerHp > 0) {
        const swing = resolveSwing(match.target, match.challenger, rng);
        effects.push({ kind: 'swing', swing });
        if (swing.hit) {
          challengerHp = Math.max(0, challengerHp - swing.damage);
          fight.noDamageTicks = 0;
        }
      }
    }

    if (targetHp <= 0 || challengerHp <= 0) {
      const targetDead = targetHp <= 0;
      const winner = targetDead ? match.challenger : match.target;
      const loser = targetDead ? match.target : match.challenger;
      fight.winner = winner;
      fight.loser = loser;
      match.phase = 'ending';
      fight.phaseTicks = 0;
      effects.push({ kind: 'death', winner, loser });
      return effects;
    }

    if (fight.noDamageTicks >= DUEL_NO_DAMAGE_DRAW_TICKS) {
      effects.push({ kind: 'end', outcome: 'draw', winner: null, loser: null });
    }
    return effects;
  }

  if (match.phase === 'ending') {
    fight.phaseTicks += 1;
    if (fight.phaseTicks >= DUEL_DEATH_BEAT_TICKS) {
      effects.push({ kind: 'end', outcome: 'win', winner: fight.winner, loser: fight.loser });
    }
    return effects;
  }

  return effects;
}

/**
 * The outcome of a {@link fireDuelBow} attempt: either a resolved `shot` for the
 * server to animate (the shoot clip on the shooter) and, on a hit, apply damage
 * for; or a `rejected` no-op with the reason the shot was refused. A rejection is
 * silent to the shooter (the server just logs it) - the same discipline as an
 * out-of-range auto-swing being skipped.
 */
export type DuelBowResult =
  | { kind: 'shot'; swing: DuelSwing }
  | {
      kind: 'rejected';
      reason: 'not-fighting' | 'not-participant' | 'out-of-range' | 'cooling-down' | 'combatant-down';
    };

/**
 * Resolve one bow shot in a live duel (Fire your bow!, ADR-0255). Unlike the
 * tick-driven melee auto-swing in {@link stepDuel}, a bow shot is a **self-contained**
 * decision the server drives outside the per-tick lifecycle: once from the arming F
 * press and then once per tick while bow auto-fire stays armed (Auto-fire the duel
 * bow, ADR-0279). It mirrors the auto-swing's
 * resolution exactly - the same 50/50 {@link DUEL_HIT_CHANCE} and the same
 * {@link DUEL_DAMAGE_MIN}..{@link DUEL_DAMAGE_MAX} damage from the injected `rng`
 * (the server's `duelRng` seam; a test pins a scripted one) - so a bow and a fist
 * hit for the same numbers; only the range and the trigger differ.
 *
 * Gates, in order: the match must be **fighting** (no firing during the countdown,
 * the death beat, or a pending challenge), the `shooter` must be one of the two
 * duelists, **both duelists must be alive** (`shooterHealth`/`opponentHealth` both
 * `> 0`), the opponent must be within {@link DUEL_BOW_RANGE} (`distance` is the
 * Chebyshev tile distance the server computes), and the shooter's bow cooldown must
 * be ready. The **both-alive gate mirrors the melee auto-swing** ({@link stepDuel}
 * lands a swing only when `challengerHp > 0 && targetHp > 0`), so a duelist reduced
 * to 0 HP this tick looses no "dead man's shot" retaliation, and no shot is fired at
 * a just-downed corpse (Auto-fire the duel bow, ADR-0279); the server passes the live
 * replicated healths. On a fired shot it stamps that shooter's cooldown to a full
 * {@link DUEL_BOW_COOLDOWN_TICKS} and, on a **hit**, resets the no-damage draw
 * watchdog (a landed arrow keeps the duel alive) - the same `noDamageTicks = 0`
 * reset a landed auto-swing does. The server applies the damage through the shared
 * combat seam; **death is not decided here** - the next {@link stepDuel} tick reads
 * the reduced health and emits the `death` effect, so a bow kill and a fist kill
 * run the identical death/payout path.
 */
export function fireDuelBow(
  match: DuelMatch,
  shooter: EntityId,
  distance: number,
  shooterHealth: number,
  opponentHealth: number,
  rng: () => number,
): DuelBowResult {
  const fight = match.fight;
  if (!fight || match.phase !== 'fighting') return { kind: 'rejected', reason: 'not-fighting' };
  const isChallenger = shooter === match.challenger;
  const isTarget = shooter === match.target;
  if (!isChallenger && !isTarget) return { kind: 'rejected', reason: 'not-participant' };
  if (shooterHealth <= 0 || opponentHealth <= 0) return { kind: 'rejected', reason: 'combatant-down' };
  if (distance > DUEL_BOW_RANGE) return { kind: 'rejected', reason: 'out-of-range' };
  const cooldown = isChallenger ? fight.challengerBowCooldownTicks : fight.targetBowCooldownTicks;
  if (cooldown > 0) return { kind: 'rejected', reason: 'cooling-down' };

  const opponent = isChallenger ? match.target : match.challenger;
  const swing = resolveSwing(shooter, opponent, rng);
  if (isChallenger) fight.challengerBowCooldownTicks = DUEL_BOW_COOLDOWN_TICKS;
  else fight.targetBowCooldownTicks = DUEL_BOW_COOLDOWN_TICKS;
  if (swing.hit) fight.noDamageTicks = 0;
  return { kind: 'shot', swing };
}

/**
 * Toggle a duelist's **bow auto-fire** arming (Auto-fire the duel bow) and return
 * the new armed state. Pressing F is a *toggle*, not a one-shot: the first press
 * arms auto-fire (the server looses a shot immediately, then one every
 * {@link DUEL_BOW_COOLDOWN_TICKS} on its own while the opponent stays beyond punch
 * range), and a second press disarms it - the ranged mirror of the always-on
 * auto-swing, but opt-in because a bow is the alternative to the fist. A no-op
 * returning `false` outside a live fight or from a non-participant, so a stray F never
 * arms anything. This only flips the intent bit; the actual firing stays gated by
 * {@link fireDuelBow}'s range and per-shooter cooldown, so toggling on cannot bypass
 * the cadence. The switch is **one-way**: the server also auto-clears this flag via
 * {@link disarmBowAutoFire} the instant the opponent enters punch range (bow -> fists),
 * and it never re-arms on its own - re-arming is always a fresh F press.
 */
export function toggleBowAutoFire(match: DuelMatch, shooter: EntityId): boolean {
  const fight = match.fight;
  if (!fight || match.phase !== 'fighting') return false;
  if (shooter === match.challenger) return (fight.challengerBowAutoFire = !fight.challengerBowAutoFire);
  if (shooter === match.target) return (fight.targetBowAutoFire = !fight.targetBowAutoFire);
  return false;
}

/**
 * Force a duelist's **bow auto-fire** off (Auto-fire the duel bow, one-way switch,
 * ADR-0279). The server calls this the instant the opponent enters punch range so the
 * bow auto-switches back to fists and the melee auto-swing takes over - and, crucially,
 * because the arm flag is genuinely cleared (not merely skipped for the tick), the bow
 * does **not** resume when the opponent backs off; re-arming always needs a fresh F.
 * Idempotent, and a no-op outside a live fight or for a non-participant.
 */
export function disarmBowAutoFire(match: DuelMatch, shooter: EntityId): void {
  const fight = match.fight;
  if (!fight || match.phase !== 'fighting') return;
  if (shooter === match.challenger) fight.challengerBowAutoFire = false;
  else if (shooter === match.target) fight.targetBowAutoFire = false;
}

/**
 * Whether a duelist's bow auto-fire is currently armed. `false` outside a live fight
 * or for a non-participant, so the server's per-tick driver can ask blindly. The
 * companion query to {@link toggleBowAutoFire}: the server reads this each fight tick
 * and, when armed, drives one {@link fireDuelBow} through the same range/cooldown gates
 * the manual shot used - so an armed-but-out-of-range or armed-but-cooling bow simply
 * skips, exactly the way the auto-swing skips while the opponent is kited away.
 */
export function bowAutoFireArmed(match: DuelMatch, shooter: EntityId): boolean {
  const fight = match.fight;
  if (!fight || match.phase !== 'fighting') return false;
  if (shooter === match.challenger) return fight.challengerBowAutoFire;
  if (shooter === match.target) return fight.targetBowAutoFire;
  return false;
}
